import { apiService } from '../../../components/api/apiService';
import { logDataModels } from './dataModelsLogger';

const CONNECTION = 'backend_py';
const PATH = '/api/v1/files/preview';

export interface FilePreviewResponse {
  fileName: string;
  mimeType: string;
  size: number;
  totalSize: number;
  truncated: boolean;
  contentBase64: string;
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || fallback;
}

export async function fetchFilePreview(input: {
  sourceId: string;
  tableName: string;
  tableId?: string;
  path?: string;
}): Promise<FilePreviewResponse> {
  logDataModels('app', 'file_preview_load', {
    sourceId: input.sourceId,
    tableName: input.tableName,
    tableId: input.tableId || '',
    hasPath: Boolean(input.path),
  });
  try {
    const data = (await apiService.post(
      PATH,
      {
        sourceId: input.sourceId,
        tableName: input.tableName,
        tableId: input.tableId || undefined,
        path: input.path || undefined,
      },
      CONNECTION,
    )) as FilePreviewResponse;
    const fileName = String(data?.fileName || input.tableName || 'file');
    const mimeType = String(data?.mimeType || 'application/octet-stream');
    const size = Number(data?.size || 0);
    const totalSize = Number(data?.totalSize || size);
    const truncated = Boolean(data?.truncated);
    const contentBase64 = String(data?.contentBase64 || '');
    if (!contentBase64) {
      throw new Error('File preview returned no content');
    }
    logDataModels('app', 'file_preview_ok', { fileName, mimeType, size, truncated, totalSize });
    return { fileName, mimeType, size, totalSize, truncated, contentBase64 };
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load file preview');
    logDataModels('error', 'file_preview_failed', { message, sourceId: input.sourceId, tableName: input.tableName });
    throw new Error(message);
  }
}

export function decodeBase64(value: string): Uint8Array {
  const cleaned = String(value || '')
    .replace(/^data:[^;]+;base64,/i, '')
    .replace(/\s/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const padded = cleaned + '='.repeat((4 - (cleaned.length % 4 || 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
